<script lang="ts">
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import {
		Card,
		CardContent,
		CardDescription,
		CardHeader,
		CardTitle
	} from "#lib/web-ui/ui/card/index.js";
	import BedDoubleIcon from "@lucide/svelte/icons/bed-double";
	import BathIcon from "@lucide/svelte/icons/bath";
	import LandPlotIcon from "@lucide/svelte/icons/land-plot";
	import CalendarIcon from "@lucide/svelte/icons/calendar";
	import MapPinIcon from "@lucide/svelte/icons/map-pin";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import PhoneIcon from "@lucide/svelte/icons/phone";
	import MailIcon from "@lucide/svelte/icons/mail";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import { formatCurrency, toDateOnly } from "#lib/shared/formatters.js";
	import {
		RentalSlugContactForm,
		RentalSlugUnitDescription,
		RentalSlugPropertyDescription,
		RentalSlugFeaturesGrid
	} from "#lib/web-ui/rental/index.js";
	import { getRentalTemplateContext } from "./rental-template-context.svelte";
	import { ListingImageGallery, MatterportTour } from "#lib/web-ui/primitives/index.js";
	import type { UnitEntity } from "#backend/modules/buildium-listings/buildium-listings.schemas.js";

	let { unit: loadedUnit }: { unit: UnitEntity } = $props();

	let retainedUnit: UnitEntity | undefined;
	const unit = $derived.by(() => {
		if (loadedUnit?.unit) retainedUnit = loadedUnit;
		return retainedUnit ?? loadedUnit;
	});

	const rentalContext = getRentalTemplateContext();
	const templateContext = rentalContext ?? {
		name: "Fergmar",
		websiteUrl: "fergmar.com"
	};

	function buildJsonLd(params: {
		name: string;
		description: string;
		url: string;
		image: string;
		address: typeof unit.unit.address;
		bedrooms: number | undefined;
		bathrooms: number | undefined;
		sqft: number | null;
		rent: number | null;
		availableDate: Date | null;
		isAvailable: boolean;
		landlordName: string;
		landlordPhone: string | undefined;
		landlordEmail: string | undefined;
	}) {
		const jsonLd: Record<string, unknown> = {
			"@context": "https://schema.org",
			"@type": "Apartment",
			"@id": params.url,
			name: params.name,
			description: params.description,
			url: params.url,
			address: {
				"@type": "PostalAddress",
				streetAddress: params.address.addressLine2
					? `${params.address.addressLine1} ${params.address.addressLine2}`
					: params.address.addressLine1,
				addressLocality: params.address.city,
				addressRegion: params.address.state,
				postalCode: params.address.postalCode,
				addressCountry: "US"
			}
		};

		if (params.image) jsonLd.image = params.image;
		if (params.bedrooms !== undefined) jsonLd.numberOfBedrooms = params.bedrooms;
		if (params.bathrooms !== undefined) jsonLd.numberOfBathroomsTotal = params.bathrooms;
		if (params.sqft) {
			jsonLd.floorSize = {
				"@type": "QuantitativeValue",
				value: params.sqft,
				unitCode: "FTK"
			};
		}

		if (params.isAvailable && params.rent) {
			jsonLd.offers = {
				"@type": "Offer",
				businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
				priceSpecification: {
					"@type": "UnitPriceSpecification",
					price: params.rent,
					priceCurrency: "USD",
					unitText: "MONTH"
				},
				availability: "https://schema.org/InStock",
				...(params.availableDate && {
					availabilityStarts: toDateOnly(params.availableDate)
				})
			};
		} else if (!params.isAvailable) {
			jsonLd.offers = {
				"@type": "Offer",
				businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
				availability: "https://schema.org/OutOfStock"
			};
		}

		if (params.landlordName || params.landlordPhone || params.landlordEmail) {
			jsonLd.landlord = {
				"@type": "Organization",
				name: params.landlordName,
				...(params.landlordPhone && { telephone: params.landlordPhone }),
				...(params.landlordEmail && { email: params.landlordEmail })
			};
		}

		return JSON.stringify(jsonLd);
	}

	const listing = $derived.by(() => {
		if (!unit?.unit) return null;
		const { address, unitBedrooms, unitBathrooms, unitSize, unitNumber } = unit.unit;
		const bedLabel = unitBedrooms?.label ?? "Studio";
		const bathLabel = unitBathrooms?.label ?? "Varies";
		const sizeDesc = unitSize ? `${unitSize.toLocaleString()}sf` : "Varies";
		const bedKeyword = unitBedrooms ? `${unitBedrooms.count} bedroom apartment` : "apartment";
		const details = `${bedLabel} | ${bathLabel}`;
		const keywords = `${address.city} rentals, ${bedKeyword}, ${address.postalCode} housing, ${templateContext.name}, ${address.addressLine1}, rental property`;

		switch (unit.marketStatus) {
			case "onMarket": {
				const { listing: l, property, unit: u } = unit;
				const contact = l.contact;
				const availabilityText = l.availableDate
					? `Available ${l.availableDate.toLocaleDateString()}`
					: "Call for details";

				const displayUnit =
					unitNumber && unitNumber !== address.addressLine1 ? ` ${unitNumber}` : "";
				const siteSuffix =
					property.name !== templateContext.name ? ` | ${templateContext.name}` : "";
				const meta = {
					title: `${property.name} - ${address.addressLine1}${displayUnit}${siteSuffix}`,
					description: `Unit ${unitNumber} at ${address.addressLine1} - ${bedLabel}, ${bathLabel}, ${sizeDesc}. ${address.city}, ${address.state}. ${availabilityText}.`,
					keywords,
					ogUrl: rentalContext
						? `https://${templateContext.websiteUrl}/for-rent/${l.slug}`
						: `https://${templateContext.websiteUrl}/properties/${property.rentalType === "Residential" ? "for-rent" : "for-lease"}/${l.slug}`,
					ogImage: u.files?.length
						? cloudinaryFetch(u.files[0].url, {
								width: 1200,
								height: 630,
								crop: "fill"
							})
						: ""
				};

				return {
					isOnMarket: true as const,
					rent: l.rent,
					slug: l.slug,
					propertyName: property.name,
					contactName: contact?.name || "Property Manager",
					contactPhone: contact?.phoneNumber,
					contactEmail: contact?.email,
					contactWebsite: contact?.website,
					availabilityText,
					rentalApplicationUrl: l.rentalApplicationUrl,
					unitFiles: u.files ?? [],
					matterportUrl: unit.matterportUrl,
					details,
					hasContact: !!(contact?.name || contact?.phoneNumber || contact?.email),
					meta,
					jsonLd: buildJsonLd({
						name: meta.title,
						description: meta.description,
						url: meta.ogUrl,
						image: meta.ogImage,
						address,
						bedrooms: unitBedrooms?.count,
						bathrooms: unitBathrooms?.count,
						sqft: unitSize,
						rent: l.rent,
						availableDate: l.availableDate,
						isAvailable: true,
						landlordName: contact?.name || templateContext.name,
						landlordPhone: contact?.phoneNumber,
						landlordEmail: contact?.email
					})
				};
			}
			case "offMarket": {
				const { property, unit: u } = unit;
				const manager = property.rentalManager;
				const contactName = manager
					? `${manager.firstName} ${manager.lastName}`.trim()
					: "Property Manager";

				const displayUnit =
					unitNumber && unitNumber !== address.addressLine1 ? ` ${unitNumber}` : "";
				const siteSuffix =
					u.buildingName !== templateContext.name ? ` | ${templateContext.name}` : "";
				const meta = {
					title: `${u.buildingName} - ${address.addressLine1}${displayUnit}${siteSuffix}`,
					description: `${bedLabel}, ${bathLabel}, ${sizeDesc} unit at ${address.addressLine1}, ${address.city}, ${address.state}. This rental unit is currently not available for rent.`,
					keywords,
					ogUrl: rentalContext
						? `https://${templateContext.websiteUrl}/for-rent/${u.slug}`
						: `https://${templateContext.websiteUrl}/properties/${property.rentalType === "Residential" ? "for-rent" : "for-lease"}/${u.slug}`,
					ogImage: ""
				};

				return {
					isOnMarket: false as const,
					rent: u.marketRent,
					slug: u.slug,
					propertyName: u.buildingName,
					contactName,
					contactPhone: manager?.phoneNumbers?.[0]?.number,
					contactEmail: manager?.email,
					contactWebsite: undefined,
					availabilityText: "Not Currently Available",
					rentalApplicationUrl: undefined,
					unitFiles: undefined,
					matterportUrl: unit.matterportUrl,
					details,
					hasContact: !!(manager?.phoneNumbers?.[0]?.number || manager?.email),
					meta,
					jsonLd: buildJsonLd({
						name: meta.title,
						description: meta.description,
						url: meta.ogUrl,
						image: meta.ogImage,
						address,
						bedrooms: unitBedrooms?.count,
						bathrooms: unitBathrooms?.count,
						sqft: unitSize,
						rent: u.marketRent,
						availableDate: null,
						isAvailable: false,
						landlordName: contactName,
						landlordPhone: manager?.phoneNumbers?.[0]?.number,
						landlordEmail: manager?.email
					})
				};
			}
		}
	});
</script>

<svelte:head>
	{#if listing}
		<title>{listing.meta.title}</title>
		<meta name="description" content={listing.meta.description} />
		<meta name="keywords" content={listing.meta.keywords} />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<meta property="og:title" content={listing.meta.title} />
		<meta property="og:site_name" content={templateContext.name} />
		<meta property="og:description" content={listing.meta.description} />
		<meta property="og:url" content={listing.meta.ogUrl} />
		<meta property="og:image" content={listing.meta.ogImage} />
		<meta property="og:type" content="website" />
		{@html `<script type="application/ld+json">${listing.jsonLd}</script>`}
	{/if}
</svelte:head>

{#if listing}
	<div class="mt-24 pb-24">
		<ListingImageGallery media={listing.unitFiles} />

		<div class="mx-auto max-w-[1400px] px-6 pt-6 lg:px-8">
			<div
				class="border-primary fixed bottom-0 left-0 z-50 w-full border-t-2 bg-neutral-900 p-4 shadow-lg sm:hidden"
			>
				<div class="flex items-center justify-between">
					<div>
						<div class="text-background font-bold">
							{formatCurrency(listing.rent)}/month
						</div>
						<div class="text-xs text-gray-300">
							{listing.details}
						</div>
					</div>
					{#if listing.rentalApplicationUrl}
						<a
							href={listing.rentalApplicationUrl}
							target="_blank"
							rel="noopener noreferrer"
							class="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded px-3 py-2 text-sm font-medium"
						>
							Apply Now
							<ExternalLinkIcon class="ml-2 h-4 w-4" />
						</a>
					{:else if listing.isOnMarket}
						<Button>Contact for Application</Button>
					{/if}
				</div>
			</div>

			<div class="grid grid-cols-1 gap-8 pt-4 lg:grid-cols-3">
				<div class="lg:col-span-2">
					<div id="property-header" class="mb-6">
						<div class="block md:hidden">
							<div class="mb-4">
								<div class="flex items-start gap-3">
									<MapPinIcon class="text-primary/70 mt-1 h-6 w-6" />
									<div>
										<h1 class="flex flex-col text-2xl">
											<span class="text-2xl font-bold"
												>{unit.unit.address.addressLine1}</span
											>
											{#if unit.unit.address.addressLine2}
												<span class="text-primary text-lg font-bold">
													{unit.unit.address.addressLine2}
												</span>
											{/if}
										</h1>
										<p class="text-lg text-gray-700">
											{unit.unit.address.city}, {unit.unit.address.state}
											{unit.unit.address.postalCode}
										</p>
									</div>
								</div>
							</div>

							<div
								class="mb-4 flex items-center justify-between rounded bg-gray-100/80 p-4"
							>
								<div>
									<span class="text-muted-foreground">Rent</span>
									<div class="text-3xl font-bold">
										{#if listing.rent}
											{formatCurrency(listing.rent)}
										{:else}
											Contact for Price
										{/if}
									</div>
									<div class="mt-1 flex items-center text-sm text-gray-700">
										<CalendarIcon class="mr-1 h-4 w-4" />
										<span>{listing.availabilityText}</span>
									</div>
								</div>

								{#if listing.rentalApplicationUrl}
									<a
										href={listing.rentalApplicationUrl}
										target="_blank"
										rel="noopener noreferrer"
										class="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded px-4 py-2 text-sm font-medium transition-colors"
									>
										Apply Now
										<ExternalLinkIcon class="ml-2 h-4 w-4" />
									</a>
								{/if}
							</div>
						</div>

						<div class="hidden md:grid md:grid-cols-2 md:gap-6">
							<div>
								<div class="flex items-start gap-3">
									<MapPinIcon class="text-primary/70 mt-1 h-6 w-6" />
									<div>
										<h1 class="flex flex-col text-2xl">
											<span class="text-2xl font-bold"
												>{unit.unit.address.addressLine1}</span
											>
											{#if unit.unit.address.addressLine2}
												<span class="text-primary text-lg font-bold">
													{unit.unit.address.addressLine2}
												</span>
											{/if}
										</h1>
										<p class="text-lg text-gray-700">
											{unit.unit.address.city}, {unit.unit.address.state}
											{unit.unit.address.postalCode}
										</p>
									</div>
								</div>
							</div>

							<div
								class="flex items-center justify-between rounded bg-gray-100/80 p-4"
							>
								<div>
									<span class="text-muted-foreground">Rent</span>
									<div class="text-3xl font-bold">
										{#if listing.rent}
											{formatCurrency(listing.rent)}
										{:else}
											Contact for Price
										{/if}
									</div>
									<div class="mt-1 flex items-center text-sm text-gray-700">
										<CalendarIcon class="mr-1 h-4 w-4" />
										<span>{listing.availabilityText}</span>
									</div>
								</div>

								{#if listing.rentalApplicationUrl}
									<a
										href={listing.rentalApplicationUrl}
										target="_blank"
										rel="noopener noreferrer"
										class="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded px-4 py-2 text-sm font-medium transition-colors"
									>
										Apply Now
										<ExternalLinkIcon class="ml-2 h-4 w-4" />
									</a>
								{/if}
							</div>
						</div>
					</div>

					<div
						class="border-border mb-8 grid grid-cols-3 gap-2 rounded border bg-white p-4 shadow-sm"
					>
						<div class="flex flex-col items-center justify-center p-2 text-center">
							<BedDoubleIcon class="text-primary mb-2 h-6 w-6" />
							<div class="text-2xl font-bold">
								{unit.unit.unitBedrooms?.label ?? "N/A"}
							</div>
							<div class="text-sm text-gray-700">Bedrooms</div>
						</div>
						<div class="flex flex-col items-center justify-center p-2 text-center">
							<BathIcon class="text-primary mb-2 h-6 w-6" />
							<div class="text-2xl font-bold">
								{unit.unit.unitBathrooms?.label ?? "N/A"}
							</div>
							<div class="text-sm text-gray-700">Bathrooms</div>
						</div>
						<div class="flex flex-col items-center justify-center p-2 text-center">
							<LandPlotIcon class="text-primary mb-2 h-6 w-6" />
							<div class="text-2xl font-bold">
								{unit.unit.unitSize?.toLocaleString() || "N/A"}
							</div>
							<div class="text-sm text-gray-700">Square Feet</div>
						</div>
					</div>

					<RentalSlugUnitDescription unitDescription={unit.unit.description} />
					{#if listing.matterportUrl}
						<MatterportTour src={listing.matterportUrl} />
					{/if}
					<RentalSlugPropertyDescription
						propertyDescription={unit.property.structureDescription}
					/>
					<RentalSlugFeaturesGrid
						unitFeatures={unit.unit.features}
						propertyFeatures={unit.property.features}
						includedInRent={unit.property.includedInRent}
					/>
				</div>

				<div class="space-y-4 lg:sticky lg:top-6">
					<Card>
						<CardHeader>
							<CardTitle>Contact Information</CardTitle>
							<CardDescription>Get in touch about this property</CardDescription>
						</CardHeader>
						<CardContent class="space-y-4">
							{#if listing.hasContact}
								<div>
									<div class="font-medium">{listing.contactName}</div>
									{#if listing.contactPhone}
										<div class="mt-2 flex items-center space-x-2">
											<PhoneIcon class="text-primary h-4 w-4" />
											<a
												href={`tel:${listing.contactPhone.replace(/\D/g, "")}`}
												class="text-primary hover:underline"
											>
												{listing.contactPhone}
											</a>
										</div>
									{/if}
									{#if listing.contactEmail}
										<div class="mt-2 flex items-center space-x-2">
											<MailIcon class="text-primary h-4 w-4" />
											<a
												href={`mailto:${listing.contactEmail}`}
												class="text-primary hover:underline"
											>
												{listing.contactEmail}
											</a>
										</div>
									{/if}
									{#if listing.contactWebsite}
										<div class="mt-2 flex items-center space-x-2">
											<ExternalLinkIcon class="text-primary h-4 w-4" />
											<a
												href={listing.contactWebsite}
												target="_blank"
												rel="noopener noreferrer"
												class="text-primary hover:underline"
											>
												Visit Website
											</a>
										</div>
									{/if}
								</div>
							{:else}
								<p class="text-gray-700">Contact information not available.</p>
							{/if}
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Get in Touch</CardTitle>
							<CardDescription
								>Have questions or ready to see it in person?</CardDescription
							>
						</CardHeader>
						<CardContent>
							<RentalSlugContactForm {unit} />
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	</div>
{/if}
